import React from 'react';

import axios from 'helpers/axios';
import config from 'helpers/config';

import Tenant from 'models/TenantModel';
import FormModes from 'helpers/FormModes';
import Toggle from 'components/common/Toggle';
import { Save, Edit, X, Calculator, Percent, Info, DollarSign } from 'lucide-react';

interface Props {}

interface State {
    companySettings: any;
    tenant: Tenant;
    formState: FormModes;
    isLoading: boolean;
}

class TaxSettings extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            companySettings: {},
            tenant: {} as Tenant,
            formState: FormModes.Preview,
            isLoading: false,
        };

        this.getValue = this.getValue.bind(this);
        this.handleChange = this.handleChange.bind(this);
    }

    componentDidMount() {
        this.getCompanySettings();
    }

    getCompanySettings() {
        axios
            .get(`${config.apiUrl}/tenant/settings`)
            .then((res) => this.setState({ tenant: res.data, companySettings: res.data.tenantSettings }));
    }

    getValue(name: string) {
        // @ts-ignore
        return this.state?.companySettings[name] || '';
    }

    handleChange(event: React.FormEvent<HTMLInputElement> | React.FormEvent<HTMLTextAreaElement>) {
        const { name, value } = event.currentTarget;

        const updateCompanySettings: any = { ...this.state.companySettings, [name]: value };
        this.setState({ companySettings: updateCompanySettings });
    }

    handleUpdate() {
        const { companySettings } = this.state;
        this.setState({ isLoading: true });
        axios
            .put(`${config.apiUrl}/tenant`, companySettings)
            .finally(() => this.setState({ isLoading: false, formState: FormModes.Preview }));
    }
    render() {
        const { formState, companySettings } = this.state;

        const taxApplicableOptions = [
            {
                name: 'isSalesTaxApplicableToLabor',
                label: 'Apply Tax to Labor',
                description: 'Include labor charges in tax calculations',
                value: companySettings?.isSalesTaxApplicableToLabor,
                icon: Calculator,
            },
            {
                name: 'isSalesTaxApplicableToParts',
                label: 'Apply Tax to Parts',
                description: 'Include parts and materials in tax calculations',
                value: companySettings?.isSalesTaxApplicableToParts,
                icon: DollarSign,
            },
            {
                name: 'isSalesTaxApplicableToFees',
                label: 'Apply Tax to Fees',
                description: 'Include service fees in tax calculations',
                value: companySettings?.isSalesTaxApplicableToFees,
                icon: Percent,
            },
        ];

        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                                <span>Settings</span>
                                <span>/</span>
                                <span className="text-blue-600 font-semibold">Tax Settings</span>
                            </nav>
                            <h2 className="text-3xl font-bold text-gray-900">Tax Configuration</h2>
                            <p className="text-gray-600 mt-1">
                                Configure sales tax rates and specify which services and items are subject to taxation.
                            </p>
                        </div>
                        <div className="flex items-center space-x-3">
                            {formState === FormModes.Preview && (
                                <button
                                    onClick={() => {
                                        this.setState({ formState: FormModes.Edit });
                                    }}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                >
                                    <Edit className="h-4 w-4" />
                                    <span>Edit Settings</span>
                                </button>
                            )}

                            {formState === FormModes.Edit && (
                                <div className="flex space-x-3">
                                    <button
                                        onClick={() => {
                                            this.setState({ formState: FormModes.Preview });
                                        }}
                                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                    >
                                        <X className="h-4 w-4" />
                                        <span>Cancel</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            this.handleUpdate();
                                        }}
                                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                                    >
                                        <Save className="h-4 w-4" />
                                        <span>Save Changes</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex-1 p-8">
                    {/* Status Banner */}
                    <div className="mb-8">
                        <div
                            className={`rounded-lg border p-4 ${
                                formState === FormModes.Edit
                                    ? 'bg-blue-50 border-blue-200'
                                    : 'bg-gray-50 border-gray-200'
                            }`}
                        >
                            <div className="flex items-center">
                                <div
                                    className={`p-2 rounded-lg ${
                                        formState === FormModes.Edit ? 'bg-blue-100' : 'bg-gray-100'
                                    }`}
                                >
                                    <Calculator
                                        className={`h-5 w-5 ${
                                            formState === FormModes.Edit ? 'text-blue-600' : 'text-gray-600'
                                        }`}
                                    />
                                </div>
                                <div className="ml-4">
                                    <h3
                                        className={`text-lg font-semibold ${
                                            formState === FormModes.Edit ? 'text-blue-900' : 'text-gray-900'
                                        }`}
                                    >
                                        {formState === FormModes.Edit
                                            ? 'Editing Tax Configuration'
                                            : 'Tax Settings Overview'}
                                    </h3>
                                    <p
                                        className={`text-sm ${
                                            formState === FormModes.Edit ? 'text-blue-700' : 'text-gray-600'
                                        }`}
                                    >
                                        {formState === FormModes.Edit
                                            ? 'Make your changes below and click Save Changes to apply them.'
                                            : 'Current tax rate and applicable services configuration.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tax Rate Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-8">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                            <div className="flex items-center">
                                <Percent className="h-5 w-5 text-gray-500 mr-3" />
                                <h3 className="text-lg font-semibold text-gray-900">Sales Tax Rate</h3>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="flex items-center space-x-4">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Tax Rate Percentage
                                    </label>
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            value={this.getValue('salesTaxRate')}
                                            name="salesTaxRate"
                                            disabled={formState === FormModes.Preview}
                                            onChange={this.handleChange}
                                            placeholder="0.00"
                                            className={`w-32 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                                                formState === FormModes.Preview
                                                    ? 'bg-gray-50 border-gray-200 text-gray-600 cursor-not-allowed'
                                                    : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                        />
                                        <span className="text-gray-500 font-medium">%</span>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Enter the sales tax rate as a percentage (e.g., 8.25 for 8.25%)
                                    </p>
                                </div>

                                {/* Tax Preview */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 min-w-[200px]">
                                    <h4 className="text-sm font-semibold text-blue-900 mb-2">Tax Preview</h4>
                                    <div className="space-y-1 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-blue-700">Subtotal:</span>
                                            <span className="text-blue-900 font-medium">$100.00</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-blue-700">
                                                Tax ({this.getValue('salesTaxRate') || '0.00'}%):
                                            </span>
                                            <span className="text-blue-900 font-medium">
                                                ${((parseFloat(this.getValue('salesTaxRate')) || 0) * 1).toFixed(2)}
                                            </span>
                                        </div>
                                        <div className="border-t border-blue-300 pt-1 mt-2">
                                            <div className="flex justify-between font-semibold">
                                                <span className="text-blue-900">Total:</span>
                                                <span className="text-blue-900">
                                                    $
                                                    {(
                                                        100 +
                                                        (parseFloat(this.getValue('salesTaxRate')) || 0) * 1
                                                    ).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tax Application Settings */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                            <div className="flex items-center">
                                <Calculator className="h-5 w-5 text-gray-500 mr-3" />
                                <h3 className="text-lg font-semibold text-gray-900">Tax Application Settings</h3>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                                Choose which services and items should include sales tax
                            </p>
                        </div>

                        <div className="divide-y divide-gray-200">
                            {taxApplicableOptions.map((option, index) => {
                                const OptionIcon = option.icon;
                                return (
                                    <div
                                        key={index}
                                        className={`p-6 ${formState === FormModes.Preview ? 'opacity-75' : ''}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-start space-x-4">
                                                <OptionIcon className="h-5 w-5 text-gray-400 mt-1" />
                                                <div>
                                                    <h4 className="text-lg font-medium text-gray-900">
                                                        {option.label}
                                                    </h4>
                                                    <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                {option.value && (
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        Enabled
                                                    </span>
                                                )}
                                                <Toggle
                                                    onChange={(newValue) =>
                                                        this.setState({
                                                            companySettings: {
                                                                ...this.state.companySettings,
                                                                [option.name]: newValue,
                                                            },
                                                        })
                                                    }
                                                    value={option.value}
                                                    name={option.name}
                                                    disabled={formState === FormModes.Preview}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Info Panel */}
                    <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex gap-4">
                            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-800">
                                <h4 className="font-semibold mb-3 text-lg">Tax Settings Information</h4>
                                <ul className="space-y-2 text-blue-700">
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Sales tax is automatically calculated and added to invoices based on your
                                        settings
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        You can choose to apply tax to labor, parts, fees, or any combination
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Tax rates should match your local and state tax requirements
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Changes to tax settings will apply to all new invoices and estimates
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default TaxSettings;
